import {
	ClickAwayListener, Divider, Grow,
	Stack, Typography
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import pages from "../../../constants/pages";
import useAuthorization from "../../../hooks/useAuthorization";
import { resetNavigationState } from "../../../redux/navigationSlice";
import { selectUser } from "../../../redux/userSlice";
import {
	CoAppEditIcon, CoAppExpandMoreIcon,
	CoAppProfileAvatar, CoAppProfileAvatarContainer, CoAppProfileContentContainer,
	CoAppProfileEmailText, CoAppProfileListText, CoAppProfileLogoutTextButton,
	CoAppProfileNameText, CoAppProfileOrgNameText, CoAppProfileResourceContainer
} from "../styled/global.styled";

export default function CoAppProfile({ handleLogout }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const user = useSelector(selectUser);

	const [expanded, setExpanded] = useState(false);
	const [rolesExpanded, setRolesExpanded] = useState(false);
	const [groupsExpanded, setGroupsExpanded] = useState(false);
	const groupsShouldExpand = user.groups && user.groups.length > 10;
	const rolesShouldExpand = user.roles && user.roles.length > 10;
	const isAuthorizedForUserManagement = useAuthorization(["MANAGE_USERS_AND_GROUPS"], true);

	const handleLogoutClick = () => {
		dispatch(resetNavigationState());
		handleLogout();
	};

	const handleGroupExpand = () => setGroupsExpanded(!groupsExpanded);

	const handleRoleExpand = () => setRolesExpanded(!rolesExpanded);

	const handleExpand = () => {
		setExpanded(!expanded);
		setRolesExpanded(false);
		setGroupsExpanded(false);
	};

	const getAvatarText = () => {
		if (!user.email) return null;
		if (user.firstName && user.lastName) {
			return user.firstName[0].toUpperCase() + user.lastName[0].toUpperCase();
		} else {
			return user.email[0].toUpperCase();
		}
	};

	const handleUserEditClick = () => {
		setExpanded(false);
		setRolesExpanded(false);
		setGroupsExpanded(false);
		const stateObj = {
			id: user.userId,
			firstname: user.firstName,
			lastname: user.lastName,
			email: user.email
		};
		navigate(pages.profile + user.userId + "?from=u", { state: stateObj });
	};

	const handleClickAway = () => {
		setExpanded(false);
		setRolesExpanded(false);
		setGroupsExpanded(false);
	};

	const sectionLabelSx = {
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: "0.08em",
		textTransform: "uppercase",
		color: "rgba(13, 31, 48, 0.45)",
		marginBottom: "6px",
		display: "block"
	};

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<div>
				<CoAppProfileAvatarContainer onClick={handleExpand}>
					<CoAppProfileAvatar>
						{getAvatarText()}
					</CoAppProfileAvatar>
					<CoAppExpandMoreIcon onClick={handleExpand} clickedopen={expanded ? "true" : "false"} />
				</CoAppProfileAvatarContainer>
				<Grow in={expanded}>
					<CoAppProfileContentContainer>
						{/* Header — avatar + name + edit */}
						<Stack
							direction="row"
							alignItems="center"
							spacing={1.25}
							sx={{ padding: "16px 16px 4px" }}
						>
							<CoAppProfileAvatar h="32px" w="32px" fs="13px">
								{getAvatarText()}
							</CoAppProfileAvatar>
							<Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
								<CoAppProfileNameText>
									{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
								</CoAppProfileNameText>
								{user.isCoAppSuperAdmin && user.organizationName ? (
									<CoAppProfileOrgNameText>{user.organizationName}</CoAppProfileOrgNameText>
								) : null}
							</Stack>
							{isAuthorizedForUserManagement ? <CoAppEditIcon onClick={handleUserEditClick} /> : null}
						</Stack>
						<CoAppProfileEmailText>{user.email}</CoAppProfileEmailText>
						<Divider sx={{ borderColor: "rgba(13, 31, 48, 0.06)" }} />

						{/* Roles + Groups sections */}
						<Stack direction="column" spacing={1.25} sx={{ padding: "12px 12px 8px" }}>
							{user.groups && user.groups.length > 0 ? (
								<CoAppProfileResourceContainer>
									<Typography component="span" sx={sectionLabelSx}>My Groups</Typography>
									{user.groups.length > 10 && !groupsExpanded ? (
										<CoAppProfileListText remaindercount={user.groups.length - 10}>
											{user.groups.map(group => group.name).slice(0, 10).join(", ")}
										</CoAppProfileListText>
									) : (
										<CoAppProfileListText>
											{user.groups.map(group => group.name).join(", ")}
										</CoAppProfileListText>
									)}
									{groupsShouldExpand ? (
										<CoAppExpandMoreIcon
											onClick={handleGroupExpand}
											usefloat="true"
											clickedopen={groupsExpanded ? "true" : "false"}
										/>
									) : null}
								</CoAppProfileResourceContainer>
							) : null}

							{user.roles && user.roles.length > 0 ? (
								<CoAppProfileResourceContainer>
									<Typography component="span" sx={sectionLabelSx}>My Roles</Typography>
									{user.roles.length > 10 && !rolesExpanded ? (
										<CoAppProfileListText remaindercount={user.roles.length - 10}>
											{user.roles.slice(0, 10).join(", ")}
										</CoAppProfileListText>
									) : (
										<CoAppProfileListText>
											{user.roles.join(", ")}
										</CoAppProfileListText>
									)}
									{rolesShouldExpand ? (
										<CoAppExpandMoreIcon
											onClick={handleRoleExpand}
											usefloat="true"
											clickedopen={rolesExpanded ? "true" : "false"}
										/>
									) : null}
								</CoAppProfileResourceContainer>
							) : null}
						</Stack>

						<Divider sx={{ borderColor: "rgba(13, 31, 48, 0.06)" }} />

						{/* Logout */}
						<div style={{ padding: "6px" }}>
							<CoAppProfileLogoutTextButton disableRipple onClick={handleLogoutClick}>
								Log out
							</CoAppProfileLogoutTextButton>
						</div>
					</CoAppProfileContentContainer>
				</Grow>
			</div>
		</ClickAwayListener>
	);
}